import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { LanguageService } from 'app/core/i18n/language.service';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { Subject, debounceTime, distinctUntilChanged, filter, skip, switchMap } from 'rxjs';
import { BusinessPartnerBySubRoute } from '../../interfaces/busines-partner-by-sub-route.interface';
import { UnassignedPartnersPanelService } from './unassigned-partners-panel.service';

@Injectable()
export class UnassignedPartnersSidebarService {
  private businessPartnerService = inject(BusinessPartnerService);
  private panelService = inject(UnassignedPartnersPanelService);
  private destroyRef = inject(DestroyRef);
  private handlerMessage = inject(HandlerMessageService);
  private i18n = inject(LanguageService);

  // === STATE SIGNALS ===
  private _data = signal<BusinessPartnerBySubRoute[]>([]);
  private _totalItems = signal<number>(0);
  private _loading = signal<boolean>(false);
  private _loadingMore = signal<boolean>(false);
  private _currentPage = signal<number>(1);
  private _hasMore = signal<boolean>(true);
  private _selectedIds = signal<Set<string>>(new Set());

  readonly pageSize = 20;

  // === SHARED FROM PANEL ===
  readonly searchTerm = this.panelService.searchTerm;
  readonly subRouteId = this.panelService.subRouteIdForListView;
  readonly subRouteData = this.panelService.subRouteData;

  // === PUBLIC COMPUTED ===
  readonly data = computed(() => this._data());
  readonly totalItems = computed(() => this._totalItems());
  readonly loading = computed(() => this._loading());
  readonly loadingMore = computed(() => this._loadingMore());
  readonly hasMore = computed(() => this._hasMore());
  readonly selectedIds = computed(() => this._selectedIds());

  // === ACTION SUBJECTS ===
  readonly loadMore$ = new Subject<void>();
  readonly reload$ = new Subject<void>();

  // === OBSERVABLES FROM SIGNALS ===
  private subRouteId$ = toObservable(this.subRouteId);
  private searchTerm$ = toObservable(this.searchTerm);

  constructor() {
    // Initial load when subRouteId changes
    this.subRouteId$
      .pipe(
        filter((id) => !!id),
        takeUntilDestroyed()
      )
      .subscribe(() => this.loadInitial());

    // Search triggers reload (reset to page 1)
    this.searchTerm$
      .pipe(debounceTime(400), distinctUntilChanged(), skip(1), takeUntilDestroyed())
      .subscribe(() => this.loadInitial());

    // Load more triggers append
    this.loadMore$
      .pipe(
        filter(() => this._hasMore() && !this._loadingMore()),
        switchMap(() => {
          this._loadingMore.set(true);
          const nextPage = this._currentPage() + 1;
          return this.fetchPage(nextPage);
        }),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (response) => {
          const newData = response.data.data;
          this._data.update((current) => [...current, ...newData]);
          this._currentPage.update((p) => p + 1);
          this._hasMore.set(newData.length === this.pageSize);
          this._loadingMore.set(false);
        },
        error: (err) => {
          this._loadingMore.set(false);
          this.showLoadError(err);
        },
      });

    // Manual reload
    this.reload$.pipe(takeUntilDestroyed()).subscribe(() => this.loadInitial());
  }

  // === METHODS ===
  private loadInitial(): void {
    const subRouteId = this.subRouteId();
    if (!subRouteId) return;

    this._loading.set(true);
    this._currentPage.set(1);
    this._data.set([]);
    this._hasMore.set(true);

    this.fetchPage(1)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const data = response.data.data;
          this._data.set(data);
          const pageInfo = response.data.pageInfo as unknown as {
            _id: string | null;
            total: number;
          }[];
          this._totalItems.set(pageInfo[0]?.total ?? 0);
          this._hasMore.set(data.length === this.pageSize);
          this._loading.set(false);
        },
        error: (err) => {
          this._loading.set(false);
          this.showLoadError(err);
        },
      });
  }

  private showLoadError(err: { error?: { message?: string } }): void {
    this.handlerMessage.error({
      message: err?.error?.message,
      alternativeMessage: this.i18n.t('routing.SUB_ROUTE_PARTNERS.ERRORS.LOAD_FAILED'),
    });
  }

  private fetchPage(pageNumber: number) {
    const subRouteId = this.subRouteId();
    return this.businessPartnerService.findUnassignedToSubRouteBusinessPartnerWithCount(
      {},
      {
        currentPage: pageNumber,
        limitPerPage: this.pageSize,
        search: this.searchTerm(),
        sort: 'name',
        sortDirection: 'ASC',
      },
      subRouteId!,
      this.subRouteData()?.company?._id?.toString() ?? null
    );
  }

  // === SELECTION METHODS ===
  toggleSelection(id: string): void {
    this._selectedIds.update((set) => {
      const newSet = new Set(set);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  clearSelection(): void {
    this._selectedIds.set(new Set());
  }

  isSelected(id: string): boolean {
    return this._selectedIds().has(id);
  }
}
